import {
  getConfigurationTemplateSafe,
  type ConfigurationTemplate,
} from '../../../../../services/configurationTemplateManager';
import { ensureArray, parseJsonSafe } from '@utils/parsing';

import { StepType } from '../../types';
import { ParsedTemplateConfig } from './types';
import { SessionConfig } from '../../utils/experienceForm/transform';

export async function fetchTemplateOrThrow(templateId: string | number) {
  const template = await getConfigurationTemplateSafe(templateId);

  if (!template) {
    throw new Error('Template non trovato');
  }

  return template;
}

export function parseTemplateConfig(
  template: ConfigurationTemplate
): ParsedTemplateConfig {
  const templateRecord = template as unknown as Record<string, unknown>;
  const configCandidate = parseJsonSafe<Record<string, unknown>>(
    templateRecord.config as string | undefined,
    'Errore parsing config string'
  );

  const stepsSource =
    configCandidate?.steps ??
    parseJsonSafe<unknown>(template.steps, 'Errore parsing steps string') ??
    template.steps;

  return {
    config: configCandidate ?? null,
    steps: ensureArray(stepsSource),
  };
}

export const normalizeSteps = (rawSteps: unknown[]): SessionConfig['steps'] =>
  rawSteps.map((step: unknown, index: number) => {
    const stepObj = step as Record<string, unknown>;
    const normalizedId = String(stepObj?.id ?? index + 1);

    if (stepObj?.type === StepType.QUESTION) {
      const answers = Array.isArray(stepObj.answers) ? stepObj.answers : [];
      return {
        id: normalizedId,
        type: StepType.QUESTION,
        text: String(stepObj.text ?? ''),
        answers: answers.map((answer: unknown) => {
          const answerObj = answer as Record<string, unknown>;
          return {
            id: String(answerObj?.id ?? ''),
            label: String(answerObj?.label ?? ''),
            goToStepId: String(answerObj?.goToStepId ?? ''),
          };
        }),
      } as SessionConfig['steps'][number];
    }

    if (stepObj?.type === StepType.VIDEO) {
      return {
        id: normalizedId,
        type: StepType.VIDEO,
        videoId: String(stepObj.videoId ?? ''),
        audioId: String(stepObj.audioId ?? ''),
      } as SessionConfig['steps'][number];
    }

    return {
      ...stepObj,
      id: normalizedId,
    } as SessionConfig['steps'][number];
  });

export function buildFormValues({
  template,
  config,
  normalizedSteps,
  currentValues,
}: {
  template: ConfigurationTemplate;
  config: ParsedTemplateConfig['config'];
  normalizedSteps: SessionConfig['steps'];
  currentValues: SessionConfig;
}): SessionConfig {
  return {
    id: template.id?.toString() ?? currentValues.id ?? '',
    name:
      (config?.name as string | undefined) ??
      template.name ??
      currentValues.name ??
      '',
    description:
      (config?.description as string | undefined) ??
      template.description ??
      currentValues.description ??
      '',
    returnToWaitingRoom:
      (config?.returnToWaitingRoom as boolean | undefined) ??
      currentValues.returnToWaitingRoom ??
      false,
    steps: normalizedSteps,
  };
}

export function normalizeGeneratedSteps(rawSteps: unknown[]) {
  return normalizeSteps(rawSteps);
}

export function buildGeneratedFormValues({
  config,
  normalizedSteps,
}: {
  config: unknown;
  normalizedSteps: SessionConfig['steps'];
}): SessionConfig {
  const configObj = config as Record<string, unknown> | null | undefined;
  return {
    id: '',
    name: String(configObj?.name ?? ''),
    description: String(configObj?.description ?? ''),
    returnToWaitingRoom: Boolean(configObj?.returnToWaitingRoom),
    steps: normalizedSteps,
  };
}

export function extractGeneratedSteps(config: unknown) {
  const configObj = config as Record<string, unknown> | null | undefined;
  return ensureArray(configObj?.steps ?? []);
}
